import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { withCustomValidation, withError } from '@bingads-webui-react/validation';
import classnames from 'classnames';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { andComp, entityIs, propertyIs } from '@bingads-webui-campaign-react/form-error';
import { withControlErrorHandling, withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';
import { syndEnum } from '@bingads-webui-campaign-react/constants';
import { TextTitle, ValidatedPanel, getPanelErrorProps } from '@bingads-webui-campaign/wizard-jumbo-card';
import { AdGroup } from '@bingads-webui/campaign-odata-schemas';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { AdDistribution } from './ad-distribution';

const getExtraStyles = () => ({
  adDistribution: {
    '& [role="radiogroup"] .radio label': {
      fontWeight: 600,
      outline: 'none !important',
      '& input[type="radio"]': {
        outline: 'none !important',
      },
    },
  },
});

const LegacyAdDistributionWithValidation = (props) => {
  const {
    setAdDistributionView, options, getStyles, updateValidation,
  } = props;
  const classes = useDefaultStyles(getStyles);
  const activity = useContext(ActivityContext);
  const onChange = useCallback((newData) => {
    updateValidation([], { AdDistribution: newData });

    if (activity) {
      // signal activity to parent
      activity.signal(`onLegacyAdDistributionChange - ${JSON.stringify(newData)}`);
    }
  }, [activity, updateValidation]);

  return (
    <div className={classnames(classes.adDistribution, 'AdDistribution-section')} >
      <AdDistribution
        setAdDistributionView={setAdDistributionView}
        options={{
          ...options,
          onChange,
        }}
      />
    </div>
  );
};

LegacyAdDistributionWithValidation.propTypes = {
  updateValidation: PropTypes.func.isRequired,
  setAdDistributionView: PropTypes.func,
  options: PropTypes.shape({}).isRequired,
  getStyles: PropTypes.func,
};

LegacyAdDistributionWithValidation.defaultProps = {
  setAdDistributionView: _.noop,
  getStyles: getExtraStyles,
};

export const ValidatedAdDistribution = withCustomValidation(LegacyAdDistributionWithValidation);

const entityIsAdGroup = entityIs('adGroup');
export const ServerValidatedAdDistribution = withControlErrorHandling(withUnvalidatedControlErrors(withError(ValidatedAdDistribution, { filter: andComp(entityIsAdGroup, propertyIs('adDistribution')) })));

const getLocalizationMapping = (adDistribution) => {
  switch (adDistribution) {
    case syndEnum.BingYahooOnly:
      return _TL_('Microsoft sites and select traffic');
    case syndEnum.SearchPartnersOnly:
      return _TL_('Only partner traffic');
    case syndEnum.BingYahooAndSearchPartners:
    default:
      return _TL_('The entire Microsoft Advertising Network (recommended)');
  }
};

export const AdDistributionWithValidationSection = ({
  data, onValidate, i18n, icon, ...rest
}) => (
  <ValidatedPanel
    key="ad-distribution"
    title={<TextTitle titleKey={_TL_('Ad distribution')} topicKey="pop_BA_NewCampaignCreate_AdDistribution" details={i18n.getString(getLocalizationMapping(data && data.AdDistribution))} icon={icon} />}
    attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
    {...rest}
  >
    <ServerValidatedAdDistribution
      options={
        _.extend(_.defaults(_.pick(data, 'AdDistribution', 'Medium'), {
        campaignType: 'search',
        }))
      }
      validationProps={{
        schema: AdGroup,
        field: 'AdDistribution',
        onValidate,
      }}
    />
  </ValidatedPanel>
);

AdDistributionWithValidationSection.propTypes = {
  onValidate: PropTypes.func.isRequired,
  data: PropTypes.shape({
    AdDistribution: PropTypes.string,
    Medium: PropTypes.string,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  icon: PropTypes.string,
};

AdDistributionWithValidationSection.defaultProps = {
  icon: null,
};
